import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { ArrowRight, Loader2, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const HANDOFF_TARGETS = [
  'Mystro',
  'ApplyOnline',
  'Connective BrokerEngine',
  'Salestrekker',
  'Other Bank CRM',
];

/**
 * MoveToBankCRMDialog - Confirmation modal for graduating a nurture lead into the
 * broker's primary bank CRM. Calls POST /api/nurture/leads/{id}/graduate.
 */
const MoveToBankCRMDialog = ({ open, onOpenChange, lead, token, onGraduated }) => {
  const [target, setTarget] = useState(HANDOFF_TARGETS[0]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!lead?.id) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/nurture/leads/${lead.id}/graduate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ handoff_target: target, handoff_notes: notes || null }),
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      toast.success(`${lead.name} moved to ${target}`);
      onGraduated?.(data);
      onOpenChange(false);
      // reset
      setNotes('');
      setTarget(HANDOFF_TARGETS[0]);
    } catch (e) {
      toast.error('Could not move lead — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md dark:bg-slate-900 dark:border-slate-800"
        data-testid="lead-drawer-handoff-modal"
      >
        <DialogHeader>
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-crm-navy to-crm-blue text-white flex items-center justify-center mb-2">
            <Building2 className="w-5 h-5" />
          </div>
          <DialogTitle className="text-xl font-display font-extrabold tracking-tight">
            Move {lead?.name?.split(/\s+/)[0] || 'lead'} to Bank CRM?
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600 dark:text-slate-400">
            This will mark the lead as <strong className="text-crm-blue">graduated</strong> and archive them from your active nurture view.
            All notes, activity, and contact history stay accessible for reporting.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="handoff-target" className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
              Handoff Destination
            </Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger
                id="handoff-target"
                className="mt-1.5 dark:bg-slate-800 dark:border-slate-700"
                data-testid="handoff-target-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HANDOFF_TARGETS.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="handoff-notes" className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
              Handoff Notes <span className="font-normal normal-case text-slate-400">(optional)</span>
            </Label>
            <Textarea
              id="handoff-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Pre-approval ready, prefers refinance with ANZ — see uploaded docs."
              rows={3}
              className="mt-1.5 resize-none dark:bg-slate-800 dark:border-slate-700"
              data-testid="handoff-notes-textarea"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="dark:bg-slate-800 dark:border-slate-700"
            data-testid="handoff-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="bg-gradient-to-r from-crm-navy to-crm-blue hover:opacity-90 text-white shadow-md"
            data-testid="handoff-confirm"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Moving...</>
            ) : (
              <>Confirm move <ArrowRight className="w-4 h-4 ml-1.5" /></>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MoveToBankCRMDialog;
